import { BadRequestError } from "../error/errors.ts";
import type { ProjectType } from "../project/project.ts";
import type { BidRow } from "./bid-row.ts";

export const FROZEN_PRICE_MODIFICATION_ERROR_CODE = "FROZEN_PRICE_MODIFICATION_ERROR";
export const MISSING_FROZEN_PRICE_ERROR_CODE = "MISSING_FROZEN_PRICE_ERROR";
export const ALL_BID_ROWS_OPTIONAL_ERROR_CODE = "ALL_BID_ROWS_OPTIONAL_ERROR";
export const OPTIONAL_ROWS_REQUIRE_FREE_OR_ALL_FORCED_ERROR_CODE = "OPTIONAL_ROWS_REQUIRE_FREE_OR_ALL_FORCED_ERROR";
export const OPTIONAL_BID_ROWS_NOT_ALLOWED_IN_DEAL_ERROR_CODE = "OPTIONAL_BID_ROWS_NOT_ALLOWED_IN_DEAL_ERROR";

export class FrozenPriceModificationError extends BadRequestError {
  constructor(bidRowContentId: string, projectType: ProjectType) {
    super(`Cannot modify frozen price for bid row content ${bidRowContentId} in ${projectType} project`, {
      code: FROZEN_PRICE_MODIFICATION_ERROR_CODE,
    });
    this.name = "FrozenPriceModificationError";
  }
}

export class MissingFrozenPriceError extends BadRequestError {
  constructor(bidRowContentId: string, projectType: ProjectType) {
    super(`Bid row content ${bidRowContentId} must have a frozen price in ${projectType} project`, {
      code: MISSING_FROZEN_PRICE_ERROR_CODE,
    });
    this.name = "MissingFrozenPriceError";
  }
}

export class InvalidBidRowContentTypeError extends BadRequestError {
  constructor(expectedType: ProjectType, actualType: ProjectType) {
    super(`Invalid bid row content type. Expected ${expectedType} but got ${actualType}`);
    this.name = "InvalidBidRowContentTypeError";
  }
}

export class AllBidRowsOptionalError extends BadRequestError {
  constructor() {
    super("At least one bid row must be non-optional", { code: ALL_BID_ROWS_OPTIONAL_ERROR_CODE });
    this.name = "AllBidRowsOptionalError";
  }
}

export class OptionalRowsRequireFreeOrAllForcedError extends BadRequestError {
  constructor() {
    super("Optional rows require at least one free row or all rows to be forced", {
      code: OPTIONAL_ROWS_REQUIRE_FREE_OR_ALL_FORCED_ERROR_CODE,
    });
    this.name = "OptionalRowsRequireFreeOrAllForcedError";
  }
}

export class OptionalBidRowsNotAllowedInDealError extends BadRequestError {
  constructor() {
    super("Optional bid rows are not allowed in DEAL projects", {
      code: OPTIONAL_BID_ROWS_NOT_ALLOWED_IN_DEAL_ERROR_CODE,
    });
    this.name = "OptionalBidRowsNotAllowedInDealError";
  }
}

export const INVALID_QUANTITY_FORMULA_ERROR_CODE = "INVALID_QUANTITY_FORMULA_ERROR";

export const QUANTITY_FORMULA_ERROR_REASON = {
  SYNTAX: "syntax",
  DIVISION_BY_ZERO: "division-by-zero",
  MISSING_SOURCE: "missing-source",
  /** Over QUANTITY_FORMULA_MAX_TOKENS: refused before the recursive evaluation could overflow the stack. */
  TOO_LONG: "too-long",
  /** A source carries a Quantity Formula itself, or is the row itself. */
  CHAINED_SOURCE: "chained-source",
  /** The row already sources another row's Quantity Formula. */
  USED_AS_SOURCE: "used-as-source",
} as const;

export type QuantityFormulaErrorReason =
  (typeof QUANTITY_FORMULA_ERROR_REASON)[keyof typeof QUANTITY_FORMULA_ERROR_REASON];

export class InvalidQuantityFormulaError extends BadRequestError {
  public readonly reason: QuantityFormulaErrorReason;
  /** Rows at fault, for chained-source and used-as-source. */
  public readonly bidRowIds: BidRow["id"][];

  constructor(reason: QuantityFormulaErrorReason, bidRowIds: BidRow["id"][] = []) {
    super(`Invalid quantity formula: ${reason}`, {
      code: INVALID_QUANTITY_FORMULA_ERROR_CODE,
      context: { reason, bidRowIds },
    });
    this.name = "InvalidQuantityFormulaError";
    this.reason = reason;
    this.bidRowIds = bidRowIds;
  }
}
